<template>
    <FormLayout>
        <FormLayoutRow>
            <FormLayoutLabel
                text="eventcreate.sourceevent"
                tooltip="eventcreate.sourceeventtooltip"
            ></FormLayoutLabel>
            <div class="w-100">
                <InputTypeAheadEvent
                    @item-selected="eventClicked"
                    placeholder="copyevent.fromsourcsearch"
                    :autoresize="true"
                    :eventlist="previousEvents"
                    ref="typeahed"
                />
            </div>
        </FormLayoutRow>

        <FormLayoutRow>
            <br />
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.name" />
            <InputText
                v-if="model != null && model.SourceEventId != null"
                ref="nameInput"
                :autofocus="true"
                v-model="model.EventInfo!.Name"
                :maxlength="100"
                :validator="new ValidatorDef({ Min: 0, Max: 0, Required: true })"
            />
            <InputText
                v-else
                :disabled="true"
                v-model="model.EventInfo!.Name"
            />
        </FormLayoutRow>

        <FormLayoutRow>
            <FormLayoutLabel text="eventinfobase.date" />
            <InputDateTime
                format="DATE"
                v-model="model.EventInfo!.Date"
            />
        </FormLayoutRow>
        <FormLayoutRow v-if="showProfile">
            <FormLayoutLabel text="eventinfobase.profileid" />
            <InputProfileSelect
                v-model="model.EventInfo!.ProfileID"
                :row="model.EventInfo!"
                @loadedprofilecount="onLoadedProfileCount"
            />
        </FormLayoutRow>
        <FormLayoutRow>
            <FormLayoutLabel />
            <InputBool
                v-model="model.SourceEventIncludeContestants"
                label="eventinfobase.includecontestants"
                tooltip="eventinfobase.includecontestantstooltip"
            />
        </FormLayoutRow>

        <div class="mt-5">
            <h4>{{ $tm('eventinfo.details') }}</h4>
            <LoadingComp
                :state="LoadState"
                :hideDetails="true"
                :delay="200"
            />

            <div v-if="LoadState.AllDone">
                <div
                    class="my-3"
                    v-if="isEmptyEvent"
                >
                    <AlertMessage type="warning">{{ $tm('eventinfo.emptyevent') }}</AlertMessage>
                </div>

                <ContestantEventDetailsView
                    v-if="sourceEvent"
                    :value="sourceEvent"
                    :categories="sourceCategories"
                    :stationroles="sourceStationRoles"
                    :races="sourceRaces"
                    :hide-contestants="!model.SourceEventIncludeContestants"
                />
            </div>
        </div>
    </FormLayout>
</template>
<script setup lang="ts">
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { Category, CreateEventRequest, Eventinfo, Race, StationRole, EventinfoBase, TypeAheadItem } from '@/objects/generated';
import { AlertMessage, InputBool, InputDateTime, InputProfileSelect, InputText, LoadingComp } from '@/components';
import { computed, inject, onMounted, ref, watch, type Ref } from 'vue';
import { useHelper, useTaskLoader } from '@/composeables';
import ContestantEventDetailsView from '../Contestants/ContestantEventDetailsView.vue';
import { BaseObjDao, EventDao } from '@/dao';
import { $tm } from '@/locize';
import type { FieldAlertMessage } from '@/objects';
import { ValidatorDef, type IEditorComponent } from '@/objects/gui';
import { getDateWithMaxTime, getDateWithMinTime } from '@/utilities/dateutils';
import InputTypeAheadEvent from '@/components/Typeahead/General/InputTypeAheadEvent.vue';
import type { TypeAheadExpose } from '@/components/Typeahead';

const model = defineModel<CreateEventRequest>({ required: true });

const typeahead = ref<TypeAheadExpose | null>(null);

const props = defineProps<{
    previousEvents?: EventinfoBase[] | null;
}>();

const { AddLoader, LoadState, ClearLoaders } = useTaskLoader();

model.value.SourceEventId = props.previousEvents && props.previousEvents.length > 0 ? props.previousEvents.at(0)!.ID : null;

// The event we are copying from
const sourceEvent = ref<Eventinfo | null>(null);

// Ref to the name input field
const nameInput = ref<IEditorComponent>();

// The categories from the source event
const sourceStationRoles = ref<StationRole[]>([]);
const sourceCategories = ref<Category[]>([]);
const sourceRaces = ref<Race[]>([]);

const isInitialized = ref<boolean>(false);

/**
 * Helper to check if we have loaded the event and it is empty
 */
const isEmptyEvent = computed(() => {
    return (
        isInitialized.value &&
        model.value.Categories.length === 0 &&
        model.value.Races.length === 0 &&
        sourceStationRoles.value.length === 0
    );
});

// How many profiles are loaded for the current event
const loadedprofilecount = ref<number | null>(null);

/**
 * Callback when the profile count is loaded.
 * @param count
 */
const onLoadedProfileCount = async (count: number) => {
    loadedprofilecount.value = count;
};

/**
 * If we should show the profile select box.
 */
const showProfile = computed(() => {
    return loadedprofilecount.value == null || loadedprofilecount.value != 1;
});

/**
 * Is called when a item is clicked in the typeahead
 * @param item
 * @param request
 */
const eventClicked = (item: TypeAheadItem) => {
    model.value.SourceEventId = item.ID;
};

watch(
    () => model.value.SourceEventId,
    async (nv) => {
        // We need to load the event
        ClearLoaders();
        if (nv) {
            if (model.value.EventInfo !== null) {
                model.value.EventInfo.Name = '.';
            }

            await AddLoader(async () => {
                sourceEvent.value = await EventDao.Event(nv);

                // WE set the name of the event to the selected event + _copy
                if (model.value.EventInfo && sourceEvent.value) {
                    model.value.EventInfo = sourceEvent.value.DeepClone();
                    model.value.EventInfo.SetEntityStateAsAddedAndResetId(0);
                    model.value.EventInfo.Date = new Date();
                    model.value.EventInfo.EventStart = getDateWithMinTime(model.value.EventInfo.Date);
                    model.value.EventInfo.EventEnd = getDateWithMaxTime(model.value.EventInfo.Date);
                    model.value.EventInfo.OrganizerID = sourceEvent.value.OrganizerID;
                    model.value.EventInfo.ProfileID = sourceEvent.value.ProfileID;
                    model.value.EventInfo.Name = `${sourceEvent.value.Name} - ${$tm('eventinfo.copy')}`;
                    model.value.Categories = [];
                    model.value.Races = [];
                    model.value.Contestants = [];
                    model.value.CategoryGroupConnection = [];

                    // This is only for display to show
                    sourceCategories.value = (await BaseObjDao.Load(Category, nv)) ?? [];
                    sourceRaces.value = (await BaseObjDao.Load(Race, nv)) ?? [];
                    sourceStationRoles.value = (await BaseObjDao.Load(StationRole, nv)) ?? [];

                    isInitialized.value = true;
                }
            }, '');
        }
    },
    { immediate: true },
);

onMounted(() => {
    useHelper().setFocus();
});

const quickCreateAlertMessage =
    inject<Ref<Map<string, FieldAlertMessage>>>('QuickCreateAlertMessage') ?? new Map<string, FieldAlertMessage>();

watch(
    () => nameInput.value?.validateMessage,
    (nv, ov) => {
        if (quickCreateAlertMessage != null) {
            if (nv == null) {
                // @ts-ignore - Issue with unwrapping
                quickCreateAlertMessage.value.delete('eventinfobase.name');
            } else {
                // @ts-ignore - Issue with unwrapping
                quickCreateAlertMessage.value.set('eventinfobase.name', nv);
            }
        }
    },
    { immediate: true },
);
</script>
